<template>
  <div class="dashboard-panel print-break-inside-avoid">
    <h3 class="section-title mb-4">
      <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
      Forecast Finish Editor
    </h3>

    <div v-if="items.length === 0" class="text-center py-4">
      <p class="text-gray-500">No scheduled workstreams</p>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="table-header">
          <tr>
            <th class="px-3 py-2">Development</th>
            <th class="px-3 py-2">Workstream</th>
            <th class="px-3 py-2">Planned Start</th>
            <th class="px-3 py-2">Planned Finish</th>
            <th class="px-3 py-2">Forecast Finish</th>
            <th class="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in items"
            :key="item.row_id"
            class="border-b border-gray-100 hover:bg-gray-50"
          >
            <td class="px-3 py-2 font-medium">{{ item.development }}</td>
            <td class="px-3 py-2">{{ item.workstream }}</td>
            <td class="px-3 py-2">{{ formatDate(item.planned_start) }}</td>
            <td class="px-3 py-2">{{ formatDate(item.planned_finish) }}</td>
            <td class="px-3 py-2">
              <input
                type="date"
                :value="displayValue(item)"
                @input="drafts[item.row_id] = $event.target.value"
                class="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                :disabled="saving[item.row_id]"
              />
            </td>
            <td class="px-3 py-2">
              <div class="flex items-center gap-2">
                <button
                  v-if="isDirty(item)"
                  @click="save(item)"
                  :disabled="saving[item.row_id]"
                  class="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50 transition-colors"
                >
                  {{ saving[item.row_id] ? 'Saving...' : 'Save' }}
                </button>
                <button
                  v-if="isDirty(item) && !saving[item.row_id]"
                  @click="delete drafts[item.row_id]"
                  class="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
                >
                  Cancel
                </button>
                <button
                  v-if="item.forecast_finish && !isDirty(item)"
                  @click="clearForecast(item)"
                  :disabled="saving[item.row_id]"
                  class="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-md text-xs font-medium disabled:opacity-50 transition-colors"
                >
                  {{ saving[item.row_id] ? 'Clearing...' : 'Clear' }}
                </button>
                <span v-if="saved[item.row_id]" class="text-green-600 text-xs font-medium">✓ Saved</span>
                <span v-if="errors[item.row_id]" class="text-red-600 text-xs">{{ errors[item.row_id] }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch } from 'vue'
import { api } from '../services/api.js'

const props = defineProps({
  items: { type: Array, default: () => [] }
})

const emit = defineEmits(['updated'])

const drafts = reactive({})
const saving = reactive({})
const saved = reactive({})
const errors = reactive({})

// When fresh data arrives (e.g. forecast deleted in Smartsheet), drop stale
// drafts so the datepicker reflects the current server value (blank if cleared)
watch(
  () => props.items,
  () => {
    Object.keys(drafts).forEach(key => delete drafts[key])
  }
)

const displayValue = (item) => {
  const draft = drafts[item.row_id]
  if (draft !== undefined) return draft
  return item.forecast_finish || ''
}

const isDirty = (item) => {
  const draft = drafts[item.row_id]
  if (draft === undefined) return false
  return draft !== (item.forecast_finish || '')
}

const updateForecast = async (rowId, value) => {
  saving[rowId] = true
  delete errors[rowId]
  delete saved[rowId]

  try {
    await api.put('/dashboard/forecast-finish', {
      row_id: rowId,
      forecast_finish: value,
    })
    delete drafts[rowId]
    saved[rowId] = true
    setTimeout(() => delete saved[rowId], 3000)
    emit('updated')
  } catch (err) {
    errors[rowId] = err.response?.data?.detail || err.message || 'Update failed'
  } finally {
    saving[rowId] = false
  }
}

const save = (item) => updateForecast(item.row_id, drafts[item.row_id] || null)

const clearForecast = (item) => updateForecast(item.row_id, null)

const formatDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
</script>
